import { useState, useContext, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion } from "motion/react";
import { RefreshCw, Check, AlertTriangle } from "lucide-react";
import { DashboardControlsSlotContext } from "../GRCManagerDashboard/GRCManagerDashboard";
import { GroupPicker, type ScopeItem } from "../GRCManagerDashboard/grcDashboardWidgets";
import { SkeletonList } from "../../components/Skeleton";
import {
  fetchAuditorControlFailures,
  fetchAuditorEntityRisk,
  getAPI,
  getOrgUnitTree,
  type AuditorAttribution,
  type AuditorScope,
  type OrgUnit,
} from "../../../api/frameworksApi";
import { API_URL } from "../../../constants";
import {
  toOrgRiskRow,
  type ControlFailureRow,
  type OrgRiskRow,
} from "./groupAuditorData";
import {
  ImmediateAttentionSection,
  HighestFailedControlsSection,
  ControlsHighestFailureRateSection,
  MaxPendingAssessmentsSection,
} from "./GroupAuditorDashboardSections";

type AuditorTab = "group" | "organization";

const ALL_ORGS_ID = "__all_orgs__";





type ScopeLevel = "GROUP" | "ORGANIZATION" | "DIVISION" | "DEPARTMENT" | "APPLICATION" | null;

interface FilterScope {
  level: ScopeLevel;
  items: { id: string; name: string }[];
}


function tabForLevel(level: ScopeLevel): AuditorTab {
  return level === "GROUP" ? "group" : "organization";
}


interface ScopeAnchors {
  groupId: string | null;
  groupName: string | null;
  orgId: string | null;
  orgName: string | null;
}

function findNode(nodes: OrgUnit[], id: string): OrgUnit | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const hit = findNode(node.children ?? [], id);
    if (hit) return hit;
  }
  return null;
}

function firstOrganization(node: OrgUnit | null): OrgUnit | null {
  if (!node) return null;
  if (node.orgType === "ORGANIZATION") return node;
  for (const child of node.children ?? []) {
    const hit = firstOrganization(child);
    if (hit) return hit;
  }
  return null;
}

function collectOrganizations(node: OrgUnit | null): OrgUnit[] {
  if (!node) return [];
  if (node.orgType === "ORGANIZATION") return [node];
  return (node.children ?? []).flatMap((child) => collectOrganizations(child));
}

function groupContaining(roots: OrgUnit[], id: string): OrgUnit | null {
  for (const root of roots) {
    if (root.orgType === "GROUP" && findNode([root], id)) return root;
  }
  return null;
}


function resolveAnchors(scope: FilterScope | null, roots: OrgUnit[]): ScopeAnchors {
  const anchor = scope?.items?.[0] ?? null;
  const rootGroups = roots.filter((n) => n.orgType === "GROUP");

  if (scope?.level === "GROUP" && anchor) {
    const group = findNode(roots, anchor.id) ?? rootGroups[0] ?? null;
    const org = firstOrganization(group);
    return {
      groupId: anchor.id,
      groupName: anchor.name,
      orgId: org?.id ?? null,
      orgName: org?.name ?? null,
    };
  }

  const group = anchor ? groupContaining(rootGroups, anchor.id) ?? rootGroups[0] ?? null : rootGroups[0] ?? null;
  return {
    groupId: group?.id ?? null,
    groupName: group?.name ?? null,
    orgId: anchor?.id ?? firstOrganization(group)?.id ?? null,
    orgName: anchor?.name ?? firstOrganization(group)?.name ?? null,
  };
}


function scopeParams(tab: AuditorTab, anchors: ScopeAnchors | null): AuditorScope | null {
  if (!anchors) return null;
  if (tab === "group") return anchors.groupId ? { groupId: anchors.groupId } : null;
  return anchors.orgId ? { orgId: anchors.orgId } : null;
}


export function GroupAuditorDashboard({ hideTitle = false }: { hideTitle?: boolean } = {}) {
 
  const controlsSlot = useContext(DashboardControlsSlotContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<AuditorTab>("group");
  const [anchors, setAnchors] = useState<ScopeAnchors | null>(null);

  const [scopeLevel, setScopeLevel] = useState<ScopeLevel>(null);
  const [scopeItems, setScopeItems] = useState<ScopeItem[]>([]);
  const [orgTree, setOrgTree] = useState<OrgUnit[]>([]);

  const [scopeReady, setScopeReady] = useState(false);
  const [entityRows, setEntityRows] = useState<OrgRiskRow[]>([]);
  const [controlRows, setControlRows] = useState<ControlFailureRow[]>([]);
 
  const [, setAttribution] = useState<AuditorAttribution | null>(null);
 
  const [includeDeactivated, setIncludeDeactivated] = useState(false);

  
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getAPI(API_URL.GRC_FILTER_SCOPE),
      getOrgUnitTree().catch(() => null),
    ])
      .then(([scopeRes, treeRes]) => {
        if (cancelled) return;
        const scope: FilterScope = scopeRes?.data ?? { level: null, items: [] };
        const roots: OrgUnit[] = treeRes?.data ?? [];
        setScopeLevel(scope.level);
        setScopeItems(scope.items ?? []);
        setOrgTree(roots);
        setAnchors(resolveAnchors(scope, roots));
        setTab(tabForLevel(scope.level));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "";
        if (msg !== "OFFLINE") setError("Could not resolve your audit scope. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setScopeReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);


  const load = useCallback(async (params: AuditorScope) => {
    setLoading(true);
    setError(null);
    try {
      const [risk, controls] = await Promise.all([
        fetchAuditorEntityRisk(params),
        fetchAuditorControlFailures(params),
      ]);
      setEntityRows((risk?.data?.rows ?? []).map(toOrgRiskRow));
      setControlRows(controls?.data?.rows ?? []);
      setAttribution(risk?.data?.attribution ?? null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "";
      if (msg !== "OFFLINE") setError("Could not load audit data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!scopeReady) return;
    const params = scopeParams(tab, anchors);
    if (!params) {
      setEntityRows([]);
      setControlRows([]);
      setLoading(false);
      return;
    }
    void load(params);
  }, [scopeReady, tab, anchors, load]);

  const refresh = () => {
    const params = scopeParams(tab, anchors);
    if (params) void load(params);
  };

 
  const missingAnchor = scopeReady && !error && !scopeParams(tab, anchors);

 
  const deactivatedCount = entityRows.filter((r) => !r.active).length;
  const visibleRows = includeDeactivated ? entityRows : entityRows.filter((r) => r.active);

  const showOrgPicker = scopeReady && scopeLevel === "ORGANIZATION" && scopeItems.length > 0;

  const selectOrg = (item: ScopeItem) => {
    if (item.id === anchors?.orgId) return;
    setIncludeDeactivated(false);
    setAnchors(resolveAnchors({ level: "ORGANIZATION", items: [item] }, orgTree));
  };

  const isGroupScope = scopeReady && scopeLevel === "GROUP";

  const groupOrgItems = useMemo<ScopeItem[]>(() => {
    if (!isGroupScope || !anchors?.groupId) return [];
    return collectOrganizations(findNode(orgTree, anchors.groupId)).map((o) => ({
      id: o.id,
      name: o.name,
    }));
  }, [isGroupScope, anchors?.groupId, orgTree]);

  const selectGroupOrg = (item: ScopeItem) => {
    if (item.id === ALL_ORGS_ID) {
      if (tab === "group") return;
      setIncludeDeactivated(false);
      setTab("group");
      return;
    }
    if (tab === "organization" && item.id === anchors?.orgId) return;
    setIncludeDeactivated(false);
    setTab("organization");
    setAnchors((prev) => (prev ? { ...prev, orgId: item.id, orgName: item.name } : prev));
  };

  const controls = (
    <div className="flex flex-wrap items-center gap-2">
      {isGroupScope && anchors?.groupName && (
        <span className="inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded-lg border border-indigo-300 bg-indigo-50 px-3 text-sm font-semibold text-indigo-700 dark:border-indigo-400/40 dark:bg-indigo-500/10 dark:text-indigo-300">
          {anchors.groupName}
        </span>
      )}
      {isGroupScope && (
        <GroupPicker
          label="Organization"
          items={[{ id: ALL_ORGS_ID, name: "All Organizations" }, ...groupOrgItems]}
          selectedId={tab === "group" ? ALL_ORGS_ID : anchors?.orgId ?? null}
          selectedName={tab === "group" ? "All Organizations" : anchors?.orgName ?? ""}
          onSelect={selectGroupOrg}
          emptyText="No organizations in this group"
        />
      )}
      {showOrgPicker && (
        <GroupPicker
          label="Organization"
          items={scopeItems}
          selectedId={anchors?.orgId ?? null}
          selectedName={anchors?.orgName ?? ""}
          onSelect={selectOrg}
        />
      )}
      {deactivatedCount > 0 && (
        <button
          type="button"
          onClick={() => setIncludeDeactivated((v) => !v)}
          title={
            `${deactivatedCount} ${entityLabel(tab).toLowerCase()}${deactivatedCount === 1 ? "" : "s"} in this scope ` +
            `${deactivatedCount === 1 ? "has" : "have"} a deactivated org unit. Their findings are real and still counted ` +
            "by the control-level widgets; this toggle controls whether they appear as rows."
          }
          className={`flex h-9 items-center gap-2 rounded-lg border px-3 text-xs font-semibold transition-colors ${
            includeDeactivated
              ? "border-indigo-400 bg-indigo-50 text-indigo-700 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-300"
              : "border-border bg-card text-foreground hover:border-indigo-300 dark:hover:border-indigo-500/40"
          }`}
        >
          <span
            className={`flex size-3.5 shrink-0 items-center justify-center rounded-[4px] border ${
              includeDeactivated ? "border-indigo-500 bg-indigo-600 text-white" : "border-muted-foreground/50"
            }`}
          >
            {includeDeactivated && <Check className="size-2.5" />}
          </span>
          Include deactivated ({deactivatedCount})
        </button>
      )}
      <button
        onClick={refresh}
        disabled={loading}
        className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
        title="Refresh data"
      >
        <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} />
        Refresh
      </button>
    </div>
  );

 
  const dataTabs = (
    <>
      {error ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-red-100 bg-gradient-to-br from-red-50 to-white px-6 py-16 text-center dark:border-red-500/[0.12] dark:from-red-500/[0.05] dark:to-transparent">
          <div className="flex size-12 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400">
            <AlertTriangle className="size-6" />
          </div>
          <p className="text-sm font-bold text-foreground">Unable to load the audit worklist</p>
          <p className="max-w-md text-xs leading-relaxed text-muted-foreground">{error}</p>
          <button
            onClick={refresh}
            disabled={loading}
            className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-card px-3.5 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-500/25 dark:text-red-400 dark:hover:bg-red-500/10"
          >
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Retrying" : "Try again"}
          </button>
        </div>
      ) : loading && entityRows.length === 0 ? (
        <AuditorDashboardSkeleton />
      ) : missingAnchor ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-border bg-card px-6 py-16 text-center">
          <p className="text-sm font-bold text-foreground">
            No {tab === "group" ? "group" : "organization"} in your audit scope
          </p>
          <p className="max-w-md text-xs leading-relaxed text-muted-foreground">
            {tab === "group"
              ? "Your access resolves to an organization with no parent group, so there is nothing to roll up at group level."
              : "No organization was found within your scope."}
          </p>
        </div>
      ) : (
        <>
          <ImmediateAttentionSection allRows={visibleRows} entityLabel={entityLabel(tab)} />

          <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
            <HighestFailedControlsSection
              allRows={visibleRows}
              entityLabel={entityLabel(tab)}
              scopeLabel={scopeLabel(tab)}
            />
            <ControlsHighestFailureRateSection
              rows={controlRows}
              entityLabel={entityLabel(tab)}
              scopeLabel={scopeLabel(tab)}
            />
          </div>

          <MaxPendingAssessmentsSection allRows={visibleRows} entityLabel={entityLabel(tab)} />
        </>
      )}
    </>
  );

  return (
    <motion.div
      id="group-auditor-dashboard"
      data-name={tab === "group" ? "Group Auditor Dashboard" : "Organization Auditor Dashboard"}
      className="space-y-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      {hideTitle && controlsSlot ? (
        createPortal(controls, controlsSlot)
      ) : (
        <motion.div
          className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-end"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {controls}
        </motion.div>
      )}

      {dataTabs}
    </motion.div>
  );
}

function entityLabel(tab: AuditorTab): string {
  return tab === "group" ? "Organization" : "Application";
}

function scopeLabel(tab: AuditorTab): string {
  return tab === "group" ? "the group" : "the organization";
}

function SkeletonCardShell({
  children,
  className = "",
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-2xl border border-border bg-card p-5 ${className}`}>{children}</div>
  );
}

function SkeletonChart({ height }: { height: number }) {
  const bars = [58, 82, 44, 71, 36, 64, 49, 77];
  return (
    <div className="flex items-end gap-3 animate-pulse" style={{ height }}>
      {bars.map((h, i) => (
        <div key={i} className="flex-1 rounded-t bg-muted" style={{ height: `${h}%` }} />
      ))}
    </div>
  );
}

const SKELETON_BUBBLES = [
  { left: 12, top: 62, size: 26 },
  { left: 23, top: 38, size: 44 },
  { left: 34, top: 71, size: 18 },
  { left: 44, top: 24, size: 34 },
  { left: 52, top: 55, size: 22 },
  { left: 63, top: 33, size: 52 },
  { left: 71, top: 68, size: 30 },
  { left: 82, top: 45, size: 20 },
  { left: 89, top: 18, size: 38 },
];

function SkeletonScatter({ height }: { height: number }) {
  return (
    <div className="animate-pulse" style={{ height }}>
      <div className="relative h-full w-full border-b border-l border-border">
        {SKELETON_BUBBLES.map((b, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-muted"
            style={{
              left: `${b.left}%`,
              top: `${b.top}%`,
              width: b.size,
              height: b.size,
              transform: "translate(-50%, -50%)",
            }}
          />
        ))}
      </div>
    </div>
  );
}

function SkeletonSectionHeader({ withPills = false }: { withPills?: boolean }) {
  return (
    <div className="animate-pulse">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-4 w-64 max-w-full rounded bg-muted" />
          <div className="h-3 w-80 max-w-full rounded bg-muted/70" />
        </div>
        <div className="h-7 w-28 shrink-0 rounded-lg bg-muted" />
      </div>
      {withPills && (
        <div className="mt-3 flex flex-wrap gap-2">
          {[72, 60, 78, 56, 128, 116].map((w, i) => (
            <div key={i} className="h-6 rounded-md bg-muted" style={{ width: w }} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AuditorDashboardSkeleton() {
  return (
    <div
      className="space-y-4"
      role="status"
      aria-busy="true"
      aria-label="Loading audit dashboard"
    >
      <SkeletonCardShell>
        <SkeletonSectionHeader withPills />
        <div className="mt-4">
          <SkeletonScatter height={260} />
        </div>
      </SkeletonCardShell>

      <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
        {[0, 1].map((i) => (
          <SkeletonCardShell key={i}>
            <SkeletonSectionHeader />
            <div className="mt-4">
              <SkeletonList rows={5} />
            </div>
          </SkeletonCardShell>
        ))}
      </div>

      <SkeletonCardShell>
        <SkeletonSectionHeader />
        <div className="mt-4">
          <SkeletonChart height={220} />
        </div>
      </SkeletonCardShell>

      <span className="sr-only">Loading audit dashboard data</span>
    </div>
  );
}
